<template>
  <div
    v-if="interval.requires_monitor_feedback"
    class="border-l-4 flex border-grey-700"
    :style="widthCalc(interval)"
    :class="{
      'bg-primary-200':
        interval.partner_can_upload_files && canEdit
    }"
  >
    <button
      v-if="interval.partner_can_upload_files && canEdit"
      class="w-full hover:bg-primary-400 transition-all flex justify-center items-center transition-normal focus:outline-none focus:shadow-inner"
      @click="showDocuments = true"
    >
      <icon
        icon="folder-add"
        class="icons-white w-6 h-6"
      />
      <span class="sr-only">Upload</span>
      <span
        v-if="uploadedDocuments.length"
        class="bg-primary-900 text-primary-100 p-px rounded-full w-3 h-3 -ml-2 flex items-center justify-center text-3xs"
      >
        {{ uploadedDocuments.length }}
      </span>
    </button>
    <button
      v-else-if="documents.length"
      class="w-full hover:bg-grey-700 transition-all flex justify-center items-center transition-normal focus:outline-none focus:shadow-inner"
      @click="showDocuments = true"
    >
      <icon
        icon="folder"
        class="icons-white w-6 h-6"
      />
      <span class="sr-only">View Documents</span>
      <span
        v-if="uploadedDocuments.length"
        class="bg-white p-px rounded-full w-3 h-3 -ml-2 flex items-center justify-center text-3xs"
      >
        {{ uploadedDocuments.length }}
      </span>
    </button>
    <portal to="modals">
      <app-modal
        v-if="showDocuments"
        @close="showDocuments = false"
      >
        <div class="card is-borderless rounded">
          <h2
            class="border-b border-primary-700 rounded-t bg-primary-500 text-white px-4 py-4 text-base min-w-screen sm:min-w-sm md:min-w-md"
          >
            {{ interval.date }} Progress Documents
          </h2>
          <form
            v-if="interval.partner_can_upload_files && canEdit"
            class="p-4 border-b bg-grey-100"
          >
            <input
              ref="uploads"
              type="file"
              name="support_document[]"
              :disabled="uploading"
              accept="image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf"
              multiple
              @change="addFiles"
            />
            <p class="text-xs italic mt-2">
              <strong>Max file size:</strong> 12mb
              <strong>File types:</strong> Word, Excel, PowerPoint, PDF, Images
            </p>
          </form>
          <ul class="list-reset relative flex w-full flex-row">
            <collapse-transition
              group
              tag="div"
              class="w-full"
            >
              <!-- Failed Uploads -->
              <li
                v-for="(doc, index) in failedUploads"
                :key="index"
                class="w-full text-negative-700 bg-negative-100 flex p-4 border-b border-negative-200"
              >
                <icon
                  icon="important"
                  class="icons-negative w-6 h-6 block mr-4"
                />
                <span class="block leading-tight">
                  <strong class="whitespace-no-wrap overflow-hidden">{{
                    doc.name
                  }}</strong>
                  <small class="text-xs block">{{ doc.errors[0] }}</small>
                </span>
              </li>
              <!-- Processing Documents -->
              <li
                v-for="(doc, index) in processing"
                :key="index"
                class="w-full text-primary-700 bg-primary-100 flex p-4 border-b border-primary-200"
              >
                <icon
                  icon="cloud-upload"
                  class="icons-primary w-6 h-6 block mr-4"
                />
                <span class="block leading-tight">
                  <strong class="whitespace-no-wrap overflow-hidden">{{
                    doc.name
                  }}</strong>
                  <small class="text-xs block">Uploading...</small>
                </span>
              </li>
              <!-- Uploaded Documents -->
              <li
                v-for="(document, index) in uploadedDocuments"
                :key="document.id"
                class="w-full"
                :class="{
                  'bg-negative-100 text-negative-800': deleting === document.id
                }"
              >
                <div
                  class="flex text-black p-4"
                  :class="{
                    'border-b': index + 1 !== uploadedDocuments.length
                  }"
                >
                  <button
                    v-if="interval.partner_can_upload_files && canEdit"
                    class="mr-4"
                    :disabled="deleting === document.id"
                    @click="removeFile(document.id)"
                  >
                    <icon
                      icon="close-circle"
                      class="icons-negative w-6 h-6 block"
                    />
                    <span class="sr-only">Delete</span>
                  </button>
                  <span class="block leading-tight">
                    <strong class="whitespace-no-wrap overflow-hidden">{{
                      document.name
                    }}</strong>
                    <small
                      class="text-xs block"
                    >{{ document.size }}
                      <template
                        v-if="nameLookup && nameLookup[document.created_by]"
                      >
                        &ndash; Uploaded by:
                        {{ nameLookup[document.created_by] }}
                      </template>
                    </small>
                  </span>
                  <a
                    :href="downloadRoute(document.id)"
                    class="ml-auto text-sm no-underline hover:underline"
                    target="_blank"
                  >
                    [Download]
                  </a>
                </div>
              </li>
            </collapse-transition>
          </ul>
        </div>
        <button
          class="block mx-auto my-4 text-white text-sm no-underline hover:underline hover:text-primary-200 font-bold"
          @click="showDocuments = false"
        >
          Close
        </button>
      </app-modal>
    </portal>
  </div>
</template>

<script>
import { CollapseTransition } from "vue2-transitions";
export default {
  name: "ProgressReportDocument",
  components: {
    CollapseTransition
  },
  props: {
    canEdit: {
      type: [Boolean, Function],
      default: false
    },
    monitoringMonth: {
      type: Boolean,
      default: true
    },
    documents: {
      type: Array,
      default() {
        return [];
      }
    },
    interval: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      deleting: null,
      uploading: false,
      processing: [],
      uploadedDocuments: [],
      failedUploads: [],
      showDocuments: false
    };
  },
  computed: {
    nameLookup() {
      if (!window._partners && !window._monitors) return false;
      return Object.assign({}, window._partners, window._monitors);
    }
  },
  created() {
    this.uploadedDocuments = this.documents;
  },
  methods: {
    widthCalc(interval) {
      const items = [true, interval.requires_monitor_feedback, interval.requires_msp_feedback].filter(Boolean);
      return {
        width: `${items.length * 3}.25rem`
      };
    },
    downloadRoute(id, project = true) {
      let append = project ? "?project=1" : "";
      return (
        route("applications.files.download", {
          application: window._project,
          file: id
        }) + append
      );
    },
    removeFile(id) {
      if (!this.canEdit || !this.interval.partner_can_upload_files) return;
      this.failedUploads = [];
      this.deleting = id;
      axios
        .delete(
          route("applications.files.delete", {
            application: window._project,
            file: id
          })
        )
        .then(() => {
          this.uploadedDocuments = [...this.uploadedDocuments].filter(
            file => file.id !== id
          );
        })
        // eslint-disable-next-line no-console
        .catch(error => console.log(error))
        .then(() => (this.deleting = null));
    },
    validFile(file) {
      const errors = [];
      if (file.size / 1024 / 1024 > 12)
        errors.push("This file exceeds the upload limit of 12mb");
      if (errors.length) {
        file["errors"] = errors;
      }
      return file;
    },
    addFiles() {
      if (!this.canEdit || !this.interval.partner_can_upload_files) return;
      this.uploading = [];
      this.failedUploads = [];
      this.uploading = true;

      const inputFiles = this.$refs.uploads.files;
      let formData = new FormData();

      formData.append("parent_key", "progress_document");
      formData.append("key", this.interval.id);
      formData.append("extended_path", this.interval.id);

      for (let i = 0; i < inputFiles.length; i++) {
        let file = this.validFile(inputFiles[i]);
        if (file.hasOwnProperty("errors")) {
          this.failedUploads.push(file);
        } else {
          formData.append(`${this.interval.id}[${i}]`, file);
          this.processing.push(file);
        }
      }

      axios
        .post(route("applications.upload", window._project), formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(({ data }) => {
          this.uploadedDocuments = [...data, ...this.uploadedDocuments];
        })
        .catch(({ response }) => {
          const fileList = this.processing.map(file => {
            file["errors"] = [
              `Unabled to upload (${response.status}: ${response.statusText})`
            ];
            return file;
          });
          this.failedUploads = [...fileList, ...this.failedUploads];
        })
        .then(() => {
          formData = null;
          this.$refs.uploads.setAttribute("files", null);
          this.$refs.uploads.setAttribute("value", "");
          this.uploading = false;
          this.processing = [];
        });
    }
  }
};
</script>
